import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRequest } from 'ahooks';
import classnames from 'classnames';
import { history } from 'umi';
import { SortableColumn, SortBy } from 'tea-component/lib/table/addons/sortable';
import { Bubble, Button, ConfigProvider, Dropdown, List, PagingQuery, Table, TableColumn, Text } from 'tea-component';
import { getSqlViewTable, getSqlViewScheme } from '../service';

import SvgIcon from '@home/components/SvgIcon';
import FilterTabs from './FilterTabs';
import DataTableDetail from './DataTableDetail';

import { SYSTEM_NAME } from '@dbl/core/global';
import { getLocalText, updateUrlParams, useDblIntl } from '@/pages/datalab/utils/utils';
import { TableHoverFiledSearchBox } from '../../../Home/RecentlyAndShare';
import DeleteModal from './DeleteModal';
import SqlViewModal from './SqlViewModal';
import AccessSettingModal from '@/pages/datalab/components/AccessSettingModal';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { getLanguage } from '@home/utils/common';
import BubbleEllipsis from "@/pages/datalab/components/BubbleEllipsis";
import './DataTable.scss';

// 表格分页
interface PageType {
  pageSize: number;
  pageIndex: number;
}

// 上传参数请求
interface TableRequestParams {
  game_code: string; // 游戏编码
  category: string; // 类目
  cycles: string[]; // 表周期
  keywords: string | undefined; // 搜索内容
  creators: string[]; // 创建人
  labels: string[]; // 标签
  chart_names: string[]; // 图表名称
  sorts: { key: string; value: string; }[]; // 排序
  page: number; // 分页页码
  page_size: number; // 每页条数
}

// 表格Props
interface DataTableProps {
  gameCode: string;
  searchWords?: string | undefined;
  selectTables: string[];
  setSelectTables: Function;
  clearSearchWords: Function;
  className?: string;
  visible: boolean;
  setVisible: Function;
  isEdit: boolean;
  setIsEdit: Function;
}

// 排序列定义
const sortColumns: SortableColumn[] = [
  { key: 'table_name', prefer: 'asc' },
  { key: 'create_time_str', prefer: 'asc' },
  { key: 'use_count', prefer: 'desc' },
];

const { pageable, scrollable, sortable, autotip } = Table.addons;
function DataTable(props: DataTableProps) {
  const { gameCode, searchWords, selectTables, setSelectTables, className, clearSearchWords, visible, setVisible, isEdit, setIsEdit } = props || {};
  const { dblFormatMessage: dataFormatMessage, commonFormatMessage, isEn } = useDblIntl('sql_view');
  const { query } = history.location;
  const [isInit, setIsInt] = useState(true); // 是否初始化
  const [filterValue, setFilterValue] = useState(JSON.parse(query?.filterValue || '{  "category": "all" }')); // 类别筛选的值
  const [translatorMap, setTranslatorMap] = useState({}); // 中英文翻译映射
  const [filterData, setFilterData] = useState({
    creators: [],
    labels: [],
    cycles: [],
    chart_names: [],
  });
  const [categoryData, setCategoryData] = useState({ my_count: 0, others_count: 0, share_count: 0 });
  const [records, setRecords] = useState([]);
  // 排序
  const [sort, setSort] = useState<SortBy>({ by: 'use_count', order: 'desc' });
  // 过滤器
  const [quickValue, setQuickValue] = useState({
    creators: [],
    labels: [],
    cycles: [],
    chart_names: [],
  });
  // 分页信息
  const [pageInfo, setPageInfo] = useState<PageType>({ pageSize: 10, pageIndex: 1 });
  // 总条数
  const [total, setTotal] = useState<number>(0);
  const [showTableDetail, setShowTableDetail] = useState<boolean>(false);
  const [tableDetailId, setTableDetailId] = useState<string>('');
  const [tableDetailName, setTableDetailName] = useState<string>('');
  // 删除弹窗信息
  const [deleteInfo, setDeleteInfo] = useState({ visible: false, record: {} });
  // 编辑弹窗信息
  const [editInfo, setEditInfo] = useState<any>([]);
  // 权限弹窗信息
  const [accessInfo, setAccessInfo] = useState<{
    id: number | string;
    editors: Array<{
      userName: string;
      userId: string;
      isView: boolean;
      isDevelop: boolean;
    }>;
    creatorId: string;
  }>({
    id: -1,
    editors: [],
    creatorId: '',
  });
  // 表格请求
  const { loading, error, runAsync } = useRequest(getSqlViewTable, {
    manual: true,
  }); // 使用useRequest 可以减少loading和error的额外设置代码, 且在类目快速点击切换时, 可以竞态取消，当上一次请求还没返回时，又发起了下一次请求，则会取消上一次请求

  // 根据后端返回的中英映射, 获取文本
  const getText = (key: string, value: string): string => {
    const map = translatorMap?.[key]?.[value];
    return map?.[isEn ? 'en' : 'zh'] || value || "-";
  };

  const handleOnClickCreateSearch = (key: string, value: string) => {
    const newQuickValue = { ...quickValue, [key]: value };
    requestDataTable({ pageSize: pageInfo?.pageSize, pageIndex: 1 }, sort, newQuickValue);
  };

  const { chartNamesFiterData, creatorsFilterData, cyclesFilterData, labelsFilterData } = useMemo(() => {
    const { chart_names: chartNamesFiter, creators: creatorsFilter, cycles: cyclesFilter, labels: labelsFilter } = filterData;
    const getFilterData = (data: string[]) => data?.map(item => ({ value: item, text: item || "-" }));
    return {
      chartNamesFiterData: chartNamesFiter?.map(item => ({ value: item?.task_id, text: getLanguage(isEn, item?.name, item?.name_cn) || "-" })),
      creatorsFilterData: getFilterData(creatorsFilter),
      cyclesFilterData: getFilterData(cyclesFilter)?.map(item => ({ value: item?.value, text: getText('cycle', item?.text) })),
      labelsFilterData: getFilterData(labelsFilter)?.map(item => ({ value: item?.value, text: getText('label', item?.text) })),
    };
  }, [filterData, translatorMap, isEn]);

  // 列定义
  const columns: TableColumn[] = [
    {
      key: 'table_name',
      header: dataFormatMessage('name'),
      width: 400,
      fixed: 'left',
      render: (record: any) => {
        const isInclude = selectTables?.some((item: any) => item?.table_id === record?.table_id);
        const name = getLocalText(isEn, record?.customize_name, record?.customize_name_en, record?.table_name);
        return (
          <div className="flex-c">
            <Bubble
              dark
              content={(<>{name}</>)}
              placement={'top'}
            >
              <Text style={{ maxWidth: 335 }} overflow>
                <input
                  type="checkbox"
                  checked={isInclude}
                  disabled={selectTables?.length >= 1 && !isInclude}
                  style={{ marginRight: '10px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectTables((selectTables: string[]) => {
                      if (isInclude) {
                        return selectTables?.filter((item: any) => item?.table_id !== record?.table_id) || selectTables;
                      }
                      return [...selectTables, { table_id: record.table_id, database_uuid: record.database_uuid, db_name: record.db_name }];
                    });
                  }}
                  onChange={() => {
                    // 避免控制台出现红色Waring
                  }}
                />
                {name}
              </Text>
            </Bubble>
          </div>
        );
      },
    },
    {
      key: 'table_desc',
      header: dataFormatMessage('description'),
      width: 350,
      render: (record: any) => {
        const desc = (isEn ? record.table_desc_en || record.table_desc : record.table_desc);
        return desc ? (
          <BubbleEllipsis content={desc} className={'data-management-table-desc-bubble'}>
            <Text style={{ width: '100%' }} overflow>
              {desc}
            </Text>
          </BubbleEllipsis>
        ) : '-';
      },
    },
    {
      key: 'use_count',
      header: dataFormatMessage('usage_count'),
      width: isEn ? 160 : 130,
      align: 'right',
    },
    {
      key: 'label',
      header: <div key={JSON.stringify(quickValue?.labels)}>
        <TableHoverFiledSearchBox {
          ...{
            data: labelsFilterData,
            handleOnClickCreateSearch: (value: string) => handleOnClickCreateSearch('labels', value),
            value: quickValue?.labels,
            buttonText: dataFormatMessage('label'),
          }
        } />
      </div>,
      width: 120,
      render(record) {
        const label = getText('label', record?.label);
        return <Bubble dark content={label} placement={'top'} >
          <Text style={{ width: '100%' }} overflow>
            {label}
          </Text>
        </Bubble>;
      },
    },
    {
      key: 'chart_names',
      header: <div key={JSON.stringify(quickValue?.chart_names)}>
        <TableHoverFiledSearchBox {
          ...{
            data: chartNamesFiterData,
            handleOnClickCreateSearch: (value: string) => handleOnClickCreateSearch('chart_names', value),
            value: quickValue?.chart_names,
            buttonText: dataFormatMessage('chart_names'),
          }
        } />
      </div>,
      width: isEn ? 200 : 200,
      render(record) {
        const chartNames = record?.charts?.map((item: any) => getLanguage(isEn, item?.name, item?.name_cn))?.join(',') || "-";
        return (
          <Bubble
            dark
            overlayClassName={'dbl-sql-view-chart-names-bubble'}
            content={record?.charts?.length ? <>
              <div className='dbl-sql-view-chart-names-bubble-item'>
                <div className='dbl-sql-view-chart-names-bubble-item-name'>{dataFormatMessage('chart_name')}</div>
                <div>{dataFormatMessage('creator')}</div>
              </div>
              {
                record?.charts?.map((item: any) => {
                  const { name, name_cn, user_name } = item || {};
                  return <div key={user_name} className='dbl-sql-view-chart-names-bubble-item margin-t-10'>
                    <div className='dbl-sql-view-chart-names-bubble-item-name'>{getLanguage(isEn, name, name_cn)}</div>
                    <div>{user_name}</div>
                  </div>;
                })
              }
            </> : ""} placement={'top'} >
            <Text style={{ width: '100%' }} overflow>
              {chartNames}
            </Text>
          </Bubble>
        );
      },
    },
    {
      key: 'cycle',
      header: <div key={JSON.stringify(quickValue?.cycles)}>
        <TableHoverFiledSearchBox {
          ...{
            data: cyclesFilterData,
            handleOnClickCreateSearch: (value: string) => handleOnClickCreateSearch('cycles', value),
            value: quickValue?.cycles,
            buttonText: dataFormatMessage('cycle'),
          }
        } />
      </div>,
      width: 150,
      render(record) {
        return getText('cycle', record?.cycle);
      },
    },
    {
      key: 'create_time_str',
      header: dataFormatMessage('creator_time'),
      width: 190,
    },
    {
      key: 'creator',
      header: <div key={JSON.stringify(quickValue?.creators)}>
        <TableHoverFiledSearchBox {
          ...{
            data: creatorsFilterData,
            handleOnClickCreateSearch: (value: string) => handleOnClickCreateSearch('creators', value),
            value: quickValue?.creators,
            buttonText: dataFormatMessage('creator'),
          }
        } />
      </div>,
      width: 150,
      render(record: any) {
        return <Bubble dark content={record?.creator} placement={'top'} >
          <Text style={{ width: '100%' }} overflow>
            {record?.creator}
          </Text>
        </Bubble>;
      },
    },
    {
      key: 'handle',
      header: dataFormatMessage('operation'),
      fixed: 'right',
      width: isEn ? 240 : 200,
      render: (record: any) => {
        const { analysis, read, edit, delete: deletePermission, access, table_id, customize_name, customize_name_en, table_name } = record || {};
        // analysis, read固定有权限（后端返回）
        const showMoreBtn = edit || deletePermission;
        return <div className='dbl-sql-view-actions-container'>
          {
            analysis && <Button
              className="table-btn"
              type="link"
              onClick={() => {
                buriedBtnPoint({
                  buttonId: "601901002",
                  extInfo: `{"pageName": "SQL View", "pageNameCn": "SQL视图", "id": ""}`,
                  pageId: '601901',
                  typeId: 'datalab_pc_v2',
                });
                window.open(`/${SYSTEM_NAME}/${gameCode}/Analysis/MultiAnalysis?tableId=${table_id}`);
              }}
            >
              {dataFormatMessage('analysis')}
            </Button>
          }
          {
            read && <Button
              className="table-btn"
              type="link"
              onClick={() => {
                buriedBtnPoint({
                  buttonId: "601901003",
                  extInfo: `{"pageName": "SQL View", "pageNameCn": "SQL视图", "id": ""}`,
                  pageId: '601901',
                  typeId: 'datalab_pc_v2',
                });
                const name = getLocalText(isEn, customize_name, customize_name_en, table_name);
                setTableDetailId(table_id);
                setTableDetailName(name);
                setShowTableDetail(true);
              }}
            >
              {dataFormatMessage('details')}
            </Button>
          }
          {showMoreBtn &&
            <Dropdown
              trigger='click'
              placement="bottom-end"
              boxClassName={'dbl-sql-view-actions-container-dropdown'}
              button={<>
                <div className='dbl-sql-view-actions-container-dropdown-item'>
                  <div> {dataFormatMessage('more')}</div>
                  <SvgIcon type="fill-icon-arrow" />
                </div>
              </>}
              appearance='pure'
            >
              {
                () => (
                  <List type="option">
                    {edit &&
                      (<List.Item onClick={() => {
                        buriedBtnPoint({
                          buttonId: "601901004",
                          extInfo: `{"pageName": "SQL View", "pageNameCn": "SQL视图", "id": ""}`,
                          pageId: '601901',
                          typeId: 'datalab_pc_v2',
                        });
                        setVisible(true);
                        setIsEdit(true);
                        setEditInfo([record]);
                      }}>
                        <Button
                          className="table-btn"
                          type="link"
                        >
                          {commonFormatMessage('edit')}
                        </Button>
                      </List.Item>)
                    }
                    {access &&
                      (<List.Item onClick={() => {
                        handleClickItemAccess(record);
                      }}>
                        <Button
                          className="table-btn"
                          type="link"
                        >
                          {commonFormatMessage('access')}
                        </Button>
                      </List.Item>)
                    }
                    {deletePermission &&
                      (<List.Item onClick={() => {
                        buriedBtnPoint({
                          buttonId: "601901005",
                          extInfo: `{"pageName": "SQL View", "pageNameCn": "SQL视图", "id": ""}`,
                          pageId: '601901',
                          typeId: 'datalab_pc_v2',
                        });
                        getSqlViewDetailData(record);
                      }}>
                        <Button
                          className="table-btn"
                          type="link"
                        >
                          {commonFormatMessage('delete')}
                        </Button>
                      </List.Item>)
                    }
                  </List>
                )
              }
            </Dropdown>
          }
        </div>;
      },
    },
  ];

  // 发起表格请求
  useEffect(() => {
    if (isInit) return; // 如果filterValue初始化设置分类选中态, 则不发起表格请求;
    // 搜索事件和分类筛选事件都会触发表格请求，更新了路由参数和数据状态
    // 依赖searchWords和filterValue，接口请求实际是依赖了路由的参数变化
    const newPageInfo = query?.pageInfo ? JSON.parse(query?.pageInfo) : pageInfo;
    const newSort = query?.sort ? JSON.parse(query?.sort) : sort;
    const newQuickValue = query?.quickValue ? JSON.parse(query?.quickValue) : quickValue;
    if (pageInfo.pageIndex === newPageInfo?.pageIndex) {
      newPageInfo.pageIndex = 1;
    }
    requestDataTable(newPageInfo, newSort, newQuickValue, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchWords, JSON.stringify(filterValue), isInit]);

  useEffect(() => {
    // 初始化获取分类数据，判断选中tab
    getCategoryCount(true);
  }, []);

  useEffect(() => {
    if (isInit) return; // 如果filterValue初始化设置分类选中态, 则不发起表格请求;
    // 获取过滤器下拉选数据
    getTableFilterData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchWords, JSON.stringify(filterValue), isInit]);

  // 获取分类数据
  const getCategoryCount = (isInit = false) => {
    // 获取分类数据
    const params = {
      game_code: gameCode,
      keywords: searchWords,
    };
    getSqlViewScheme(params).then((res: any) => {
      setCategoryData(res || {});
      // 初始化如果我的sql有数据, 则默认选中我的sql
      // 如果路由拼接了category参数, 则不设置默认选中
      if (isInit && res?.my_count > 0 && !query?.filterValue) {
        setFilterValue({ ...filterValue, category: 'my' });
        updateUrlParams({ filterValue: { ...filterValue, category: 'my' } });
      }
      setIsInt(false);
    }).catch((e) => {
      console.log('SQL视图 分类数据请求错误: ', e);
    });
  };

  const tableFilterCacheRef = useRef<any>(null);
  // 获取过滤器数据
  const getTableFilterData = (isCache = false) => {
    const newFilterValue = query?.filterValue ? JSON.parse(query?.filterValue) : filterValue;
    // 获取分类数据
    const params = {
      game_code: gameCode,
      keywords: searchWords,
      category: newFilterValue?.category,
    };
    if (isCache && JSON.stringify(tableFilterCacheRef.current) === JSON.stringify(params)) return; // 缓存数据，避免重复请求
    tableFilterCacheRef.current = params;
    getSqlViewScheme(params).then((res: any) => {
      setFilterData(res || {});
      setTranslatorMap(res?.trans || {});
    }).catch((e) => {
      console.log('SQL视图 过滤器数据请求错误: ', e);
    });
  };

  const dataTableCacheRef = useRef<any>(null);
  /**
   * 表格请求
   * @param pageInfo 分页信息, 主要用于在切换分页条数, 跳转页码时, 传入最新分页信息
   * @param sortInfo 排序信息, 主要用于在点按排序按钮时, 传入最新排序信息
   * @param quickInfo 过滤器信息,主要用于过滤器
   * @param isCache 是否缓存数据, 默认为false, 如果为true, 则会缓存数据, 避免重复请求(fix:解决搜索重置tab，依赖重复请求的问题)
   */
  const requestDataTable = (pageInfo: any, sortInfo: any, quickInfo: any, isCache = false) => {
    setRecords([]); // 表格数据设置为空数组是为了显示tea组件自带的加载提示时, 不显示表格数据
    setPageInfo(pageInfo); // 在这里设置分页信息是因为在 类目和搜索筛选后要重置分页信息, 同时可以兼顾用户切换表格分页后的变化
    setSort(sortInfo);
    setQuickValue(quickInfo);
    const newFilterValue = query?.filterValue ? JSON.parse(query?.filterValue) : filterValue;
    setFilterValue(newFilterValue);
    updateUrlParams({ pageInfo, sort: sortInfo, filterValue: newFilterValue, keywords: searchWords, quickValue: quickInfo });
    // 上传参数
    const params: TableRequestParams = {
      game_code: gameCode,
      category: newFilterValue?.category,
      keywords: searchWords,
      creators: quickInfo?.creators || [],
      labels: quickInfo?.labels || [],
      cycles: quickInfo?.cycles || [],
      chart_names: quickInfo?.chart_names || [],
      sorts: [
        {
          key: sortInfo?.by || "use_count",
          value: sortInfo?.order || "desc",
        },
      ],
      page: pageInfo.pageIndex,
      page_size: pageInfo.pageSize,
    };
    if (isCache && JSON.stringify(dataTableCacheRef.current) === JSON.stringify(params)) return; // 缓存数据，避免重复请求
    dataTableCacheRef.current = params;
    runAsync(params)
      .then((res: any) => {
        setRecords(res?.data_list || []);
        setTotal(res?.row_count || 0);
      })
      .catch((e) => {
        console.log('SQL视图 表格请求错误: ', e);
      });
  };

  // 获取sql view数据详情
  const getSqlViewDetailData = (record: any) => {
    // 先拿表格数据设置，优化交互
    setDeleteInfo({ visible: true, record });
    // 请求接口获取真实数据（解决用户在其它页签操作过数据的问题）
    // const params = {
    //   game_code: gameCode,
    //   table_id: record?.table_id,
    // };
    // getSqlViewDetail(params).then((res: any) => {
    //   setDeleteInfo({ visible: true, record: res });
    // }).catch((e) => {
    //   setDeleteInfo({ visible: true, record });
    //   console.log('SQL视图 详情数据请求错误: ', e);
    // });
  };

  const handleClickItemAccess = (record: any) => {
    const editors: Array<{
      userName: string;
      userId: string;
      isView: boolean;
      isDevelop: boolean;
    }> = [];
    (record?.editable_person || []).forEach((person: any) => {
      if (person?.user_id === record?.user_id) return;
      const onlyRead = person?.permission?.length === 1
        ? person?.permission?.includes('read')
        : (person?.permission || []).filter((access: string) => access === 'read')?.length === 2;
      editors.push({
        userName: person?.user_name || '',
        userId: person?.user_id || '',
        isView: !!onlyRead,
        isDevelop: (person?.permission || []).length > 1,
      });
    });
    setAccessInfo({
      id: record?.table_id,
      editors,
      creatorId: record?.user_id || '',
    });
  };

  return (
    <ConfigProvider locale={isEn ? 'en' : 'zh'}>
      <FilterTabs categoryData={categoryData} filterValue={filterValue} setFilterValue={setFilterValue} />
      <Table
        recordKey="table_id"
        className={classnames(
          'data-sql-view-table',
          className,
        )}
        columns={columns}
        records={records}
        addons={[
          scrollable({
            // maxHeight: 350,
            minWidth: 1036,
          }),
          pageable({
            isPagingReset: true,
            pageSize: pageInfo.pageSize,
            pageIndex: pageInfo.pageIndex,
            pageSizeOptions: [10, 20, 50, 100],
            recordCount: total,
            stateText: dataFormatMessage('total', { total }),
            stateTextVisible: total > 10,
            pageSizeVisible: total > 10,
            pageIndexVisible: total > 10,
            jumpVisible: total > 10,
            endJumpVisible: total > 10,
            onPagingChange: (query: PagingQuery) => {
              // 请求表格数据
              requestDataTable(query, sort, quickValue);
            },
          }),
          sortable({
            columns: sortColumns,
            value: [sort],
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onChange: (value: SortBy[], context) => {
              requestDataTable(pageInfo, value[0] ?? {}, quickValue);
            },
          }),
          autotip({
            isLoading: isInit ? true : loading, // 分类未初始化完成时为loading状态
            loadingText: commonFormatMessage('loading'),
            errorText: commonFormatMessage('load_fail'),
            isError: !!error,
            isFound: Boolean(searchWords) && total === 0, // 是否显示
            foundText: commonFormatMessage('not_search'),
            clearResultText: commonFormatMessage('go_back_old'),
            onClear: () => {
              // 搜索结果为空时, 显示返回原列表按钮
              clearSearchWords?.();
            },
            style: { lineHeight: '200px' },
          }),
          // rowtooltip({
          //   tooltip: (record) =>
          //     record.sync_status === 1 ? (
          //       <div style={{ padding: '10px 14px' }}>{dataFormatMessage('tooltip')}</div>
          //     ) : null,
          // }),
        ]}
      />
      <DataTableDetail
        showTableDetail={showTableDetail}
        setShowTableDetail={setShowTableDetail}
        tableDetailId={tableDetailId}
        tableName={tableDetailName}
        gameCode={gameCode}
      />
      <DeleteModal
        gameCode={gameCode}
        deleteInfo={deleteInfo}
        setDeleteInfo={setDeleteInfo}
        deleteCallBack={() => {
          // 请求分类和下拉选数据
          getCategoryCount();
          getTableFilterData();
          const isReset = pageInfo?.pageIndex > 1 && total - 1 === (pageInfo?.pageIndex - 1) * pageInfo?.pageSize;
          requestDataTable(isReset ? { ...pageInfo, pageIndex: 1 } : pageInfo, sort, quickValue);
        }}
      />
      <SqlViewModal
        visible={visible}
        setVisible={setVisible}
        isFromEdit={isEdit}
        dataTables={editInfo}
        closeCallBack={(type: string) => {
          if (["update", "save as"]?.includes(type)) {
            // 请求分类和下拉选数据
            getCategoryCount();
            getTableFilterData();
          }
          requestDataTable(type === "save as" ? { ...pageInfo, pageIndex: 1 } : pageInfo, sort, quickValue);
          setVisible(false);
        }}
      />
      <AccessSettingModal
        visible={accessInfo.id !== -1}
        onClose={(isSuccess) => {
          setAccessInfo({ id: -1, editors: [], creatorId: '' });
          if (isSuccess) {
            requestDataTable(pageInfo, sort, quickValue);
          }
        }}
        id={accessInfo?.id as number}
        creatorId={accessInfo?.creatorId}
        editors={accessInfo?.editors}
        type='sql_view'
      />
    </ConfigProvider>
  );
}

export default DataTable;
